import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface TypeWriterProps {
  texts: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
  cursor?: boolean;
  cursorChar?: string;
  loop?: boolean;
  className?: string;
  onComplete?: () => void;
}

export const TypeWriter = ({
  texts,
  typingSpeed = 100,
  deletingSpeed = 50,
  pauseDuration = 2000,
  cursor = true,
  cursorChar = '|',
  loop = true,
  className,
  onComplete,
}: TypeWriterProps) => {
  const [displayText, setDisplayText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const currentText = texts[textIndex];

  useEffect(() => {
    if (isPaused) return;

    let timeout: ReturnType<typeof setTimeout>;

    if (isTyping) {
      // Typing phase
      if (displayText.length < currentText.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentText.slice(0, displayText.length + 1));
        }, typingSpeed);
      } else {
        // Finished typing, pause before deleting
        setIsPaused(true);
        timeout = setTimeout(() => {
          setIsPaused(false);
          if (loop || textIndex < texts.length - 1) {
            setIsTyping(false);
          } else {
            onComplete?.();
          }
        }, pauseDuration);
      }
    } else {
      // Deleting phase
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, deletingSpeed);
      } else {
        // Move to next text
        setTextIndex((prev) => (prev + 1) % texts.length);
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isTyping, isPaused, currentText, textIndex, texts, typingSpeed, deletingSpeed, pauseDuration, loop, onComplete]);

  return (
    <span className={cn('inline-flex items-center', className)}>
      <span>{displayText}</span>
      {cursor && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
          className="ml-0.5 font-light"
        >
          {cursorChar}
        </motion.span>
      )}
    </span>
  );
};

// Reveal Text - letter by letter reveal
interface RevealTextProps {
  text: string;
  delay?: number;
  staggerDelay?: number;
  className?: string;
}

export const RevealText = ({
  text,
  delay = 0,
  staggerDelay = 0.03,
  className,
}: RevealTextProps) => {
  return (
    <span className={cn('inline-flex flex-wrap', className)}>
      {text.split('').map((char, index) => (
        <motion.span
          key={`${char}-${index}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.4,
            delay: delay + index * staggerDelay,
            ease: [0.215, 0.61, 0.355, 1],
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </span>
  );
};

// Gradient Text with Animation
interface GradientTextProps {
  text: string;
  colors?: string[];
  animationDuration?: number;
  className?: string;
}

export const GradientText = ({
  text,
  colors = ['#0F52BA', '#3B7DD8', '#0A3A8A'],
  animationDuration = 8,
  className,
}: GradientTextProps) => {
  return (
    <motion.span
      className={cn('inline-block bg-clip-text text-transparent bg-gradient-to-r pb-2', className)}
      style={{
        backgroundImage: `linear-gradient(90deg, ${colors.join(', ')}, ${colors[0]})`,
        backgroundSize: '200% 100%',
      }}
      animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
      transition={{ duration: animationDuration, repeat: Infinity, ease: 'linear' }}
    >
      {text}
    </motion.span>
  );
};

// Word Rotator - cycles through words with animation
interface WordRotatorProps {
  words: string[];
  interval?: number;
  className?: string;
}

export const WordRotator = ({
  words,
  interval = 3000,
  className,
}: WordRotatorProps) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, interval);
    return () => clearInterval(timer);
  }, [words.length, interval]);

  return (
    <span className={cn('inline-block relative h-[1.2em] overflow-hidden', className)}>
      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
          className="absolute left-0"
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export default TypeWriter;
